import { makeAutoObservable, runInAction } from 'mobx';

import {
  DEFAULT_APPLICATIONS_TABLE_FILTERS,
  MAX_APPLICATIONS_PER_PAGE,
} from 'components/applications/applications.constants';
import { getSortByFields, getSortByVariables } from 'components/applications/applications.utils';
import { ApplicationsPageFilters } from 'components/applications/applications.types';

import { ApplicationApi } from './applications.api';
import { BorrowerApplicationDto } from './applications.dto';

class ApplicationsStore {
  applicationApi: ApplicationApi;

  isLoading = false;

  isNotMyCustomerLoading = false;

  isUploadContractLoading = false;

  applications: BorrowerApplicationDto[] = [];

  selectedApplication: BorrowerApplicationDto | null = null;

  filters: ApplicationsPageFilters;

  totalApplications: number;

  constructor() {
    this.applicationApi = new ApplicationApi();
    this.filters = DEFAULT_APPLICATIONS_TABLE_FILTERS;
    this.totalApplications = 0;

    makeAutoObservable(this, {}, { autoBind: true });
  }

  async getApplicationById(id: string) {
    this.isLoading = true;
    this.selectedApplication = null;

    try {
      const application = await this.applicationApi.getApplicationById(id);

      runInAction(() => {
        this.selectedApplication = application;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async sendIsNotMyCustomer(applicationId: string) {
    this.isNotMyCustomerLoading = true;

    try {
      await this.applicationApi.sendIsNotMyCustomerMessage(applicationId);
    } finally {
      runInAction(() => {
        this.isNotMyCustomerLoading = false;
      });
    }
  }

  public updateFilters = (filters: ApplicationsPageFilters) => {
    runInAction(() => {
      this.filters = filters;
    });
  };

  public updatePagination = (pageIndex: number) => {
    runInAction(() => {
      this.filters = {
        ...this.filters,
        pageIndex: pageIndex,
      };
    });
  };

  public getApplications = async () => {
    this.isLoading = true;

    try {
      const applications = await this.applicationApi.getApplications({
        offset: this.filters.pageIndex * MAX_APPLICATIONS_PER_PAGE,
        count: MAX_APPLICATIONS_PER_PAGE,
        search: this.filters.search,
        sortByFields: getSortByFields(this.filters.sortField, this.filters.isSortDesc),
        sortByVariables: getSortByVariables(this.filters.sortField, this.filters.isSortDesc),
        statusCategory: this.filters.statusCategory,
        inactiveStatus: this.filters.inactiveStatus,
      });

      runInAction(() => {
        this.applications = applications.items;
        this.totalApplications = applications.total;
      });

      return { totalApplications: applications.total };
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  public uploadContract = async (applicationId: string, files: File[]) => {
    this.isUploadContractLoading = true;

    try {
      const response = await this.applicationApi.uploadContract(applicationId, files);

      if (response.success) {
        runInAction(() => {
          this.selectedApplication = response.application;
        });
      }

      return response.success;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };
}

export default ApplicationsStore;
